import React, {FC, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {
  downloadAttachmentBlob,
  previewAttachmentBlob,
  resolveProcurementDownloadUrl,
  resolveProcurementViewUrl,
  type ProcurementAttachmentRef,
} from '../core/attachmentUtils'

type AttachmentLike = {
  id?: number | string | null
  registryId?: number | string | null
  fileName: string
  attachmentKey?: string | null
  url?: string | null
  viewUrl?: string | null
  downloadUrl?: string | null
  sizeBytes?: number | null
  available?: boolean | null
}

interface ProcurementAttachmentPrintListProps {
  attachments?: AttachmentLike[] | null
  emptyText?: string
}

const formatFileSize = (size?: number | null) => {
  if (size == null || Number.isNaN(size)) return '-'
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const ProcurementAttachmentPrintList: FC<ProcurementAttachmentPrintListProps> = ({
  attachments,
  emptyText = 'No attachments available.',
}) => {
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const list = useMemo(
    () =>
      (attachments || []).filter(
        (item) => item && String(item.fileName || '').trim()
      ),
    [attachments]
  )

  if (!list.length) {
    return <div className='text-muted'>{emptyText}</div>
  }

  return (
    <div className='table-responsive'>
      <table className='table table-bordered align-middle doc-tight mb-0 procurement-print-table'>
        <thead className='table-light'>
          <tr>
            <th style={{width: '60px'}}>No</th>
            <th>File Name</th>
            <th style={{width: '120px'}}>Size</th>
            <th style={{width: '180px'}}>Action</th>
          </tr>
        </thead>
        <tbody>
          {list.map((item, index) => {
            const ref: ProcurementAttachmentRef = {
              id: item.id ?? null,
              registryId: item.registryId ?? null,
              attachmentKey: item.attachmentKey ?? null,
              url: item.url ?? null,
              viewUrl: item.viewUrl ?? null,
              downloadUrl: item.downloadUrl ?? null,
            }
            const resolvedViewUrl = resolveProcurementViewUrl(ref)
            const resolvedDownloadUrl = resolveProcurementDownloadUrl(ref)
            const unavailable = item.available === false
            return (
              <tr key={`${item.fileName}-${index}`} className='procurement-avoid-break'>
                <td>{index + 1}</td>
                <td>{item.fileName}</td>
                <td>{formatFileSize(item.sizeBytes)}</td>
                <td>
                  <div className='d-flex gap-2 flex-wrap'>
                    <button
                      type='button'
                      className='btn btn-sm btn-light'
                      disabled={unavailable || !resolvedViewUrl}
                      onClick={async () => {
                        try {
                          setBusyKey(`${index}-view`)
                          await previewAttachmentBlob(ref)
                        } catch (error: any) {
                          toast.error('Failed to open attachment')
                        } finally {
                          setBusyKey(null)
                        }
                      }}
                    >
                      {busyKey === `${index}-view` ? 'Opening...' : 'View'}
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-light-primary'
                      disabled={unavailable || busyKey === `${index}-download` || !resolvedDownloadUrl}
                      onClick={async () => {
                        try {
                          setBusyKey(`${index}-download`)
                          await downloadAttachmentBlob(ref, item.fileName)
                        } catch (error: any) {
                          toast.error('Failed to download attachment')
                        } finally {
                          setBusyKey(null)
                        }
                      }}
                    >
                      {busyKey === `${index}-download` ? 'Downloading...' : 'Download'}
                    </button>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default ProcurementAttachmentPrintList
